import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Building2, ChevronRight, Search, Store, UserCircle2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { AuthTermsDisclosure } from '@/components/auth/auth-terms-disclosure'
import { BackendServerSelector } from '@/components/auth/backend-server-selector'
import { useSelectedBackendServerUrl } from '@/lib/backend-servers'
import { orgTreeApi } from '@/services/org-tree'
import { getAuthThemeServiceInstanceId, useAuthTheme } from '@/lib/auth-theme'
import { resolveServiceInstanceAuthLogos } from '@/lib/auth-branding'
import { TenantAuthShell } from '@/components/auth/tenant-auth-shell'
import { isRootOrgServiceInstance } from '@/lib/org-hierarchy-from-instance'

const lookupSchema = z.object({
  reference: z.string().trim().min(1, 'Organization ID or Service ID or Identifier is required'),
})

type LookupFormData = z.infer<typeof lookupSchema>

export default function SignupLookupPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const selectedBackendServerUrl = useSelectedBackendServerUrl()
  const authTheme = useAuthTheme()
  const [isLoading, setIsLoading] = useState(false)
  const searchParams = new URLSearchParams(location.search)
  const urlReference = searchParams.get('org')
    || searchParams.get('id')
    || searchParams.get('tenant')
    || searchParams.get('company_identifier')
    || searchParams.get('service_instance_id')
    || ''
  const themeReference = authTheme.locked ? getAuthThemeServiceInstanceId(authTheme) : ''
  const queryReference = themeReference || urlReference

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LookupFormData>({
    resolver: zodResolver(lookupSchema),
    defaultValues: {
      reference: queryReference,
    },
  })

  const organizationQuery = useQuery({
    queryKey: ['signup-lookup-service-instance', selectedBackendServerUrl, queryReference],
    queryFn: () => orgTreeApi.resolvePublicSignupServiceInstance(queryReference),
    enabled: queryReference.length >= 3,
    staleTime: 60_000,
    retry: false,
    placeholderData: (previousData) => previousData,
  })

  const organization = (authTheme.locked ? authTheme.organization : null) ?? organizationQuery.data
  const organizationName = organization?.name
    || organization?.title
    || organization?.identifier
    || organization?.url_identifier
    || 'Ellan One'
  const { sidePanelLogos, welcomeLogos } = resolveServiceInstanceAuthLogos(organization)
  const termsText = organization?.terms_and_conditions ?? null
  const privacyText = organization?.privacy_policy ?? null
  const primaryColor = organization?.primary_color || authTheme.primaryColor
  const loginTenant = organization?.identifier || organization?.instance_id || queryReference
  const loginHref = loginTenant
    ? `/login?tenant=${encodeURIComponent(loginTenant)}`
    : '/login'
  const vendorSignupHref = organization?.instance_id
    ? `/signup/vendor/${encodeURIComponent(organization.instance_id)}`
    : '/signup/vendor'
  const canShowOrganizationSignup = isRootOrgServiceInstance(organization) || (!authTheme.locked && !queryReference)

  useEffect(() => {
    if (!authTheme.locked || !themeReference) {
      return
    }

    navigate(`/signup/${encodeURIComponent(themeReference)}`, { replace: true })
  }, [authTheme.locked, navigate, themeReference])

  const onSubmit = async ({ reference }: LookupFormData) => {
    setIsLoading(true)
    try {
      void selectedBackendServerUrl
      const instance = await orgTreeApi.resolvePublicSignupServiceInstance(reference)
      if (!instance) {
        toast.error('No active organization was found for that Organization ID or Service ID or Identifier.')
        return
      }

      const resolvedServiceInstanceId = instance.instance_id || String(instance.id)
      navigate(`/signup/${encodeURIComponent(resolvedServiceInstanceId)}`)
    } catch {
      toast.error('Unable to load organization signup details right now.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <TenantAuthShell
      organizationName={organizationName}
      sidePanelLogos={sidePanelLogos}
      welcomeLogos={welcomeLogos}
      primaryColor={primaryColor}
      icon={UserCircle2}
      eyebrow="User signup"
      title="Join your organization"
      description=""
      panelTitle="sign up"
      panelDescription={organizationQuery.isFetching && !organization ? 'Resolving organization details...' : 'Continue with the right organization'}
      sideActions={(
        <div className="space-y-3 text-sm">
          <p className="text-primary-foreground/80">Other signup flows</p>
          <div className="flex flex-wrap gap-2">
            <Link to={vendorSignupHref} className="inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 font-semibold text-primary shadow-sm transition hover:bg-white">
              <Store className="h-4 w-4" />
              Vendor
            </Link>
            {canShowOrganizationSignup ? (
              <Link to="/signup/company" className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 font-semibold text-white ring-1 ring-white/30 transition hover:bg-white/25">
                <Building2 className="h-4 w-4" />
                Organization
              </Link>
            ) : null}
          </div>
        </div>
      )}
    >
      <Card className="border-0 bg-transparent shadow-none">
        <CardHeader className="sr-only">
          <CardTitle>Sign up</CardTitle>
          <CardDescription>Load organization signup configuration.</CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          <BackendServerSelector disabled={isLoading} className="mb-4" />
          {organization ? (
            <div className="mb-5 rounded-2xl bg-primary/10 px-4 py-3 text-center text-sm text-muted-foreground">
              Continuing signup for <span className="font-semibold text-foreground">{organizationName}</span>.
            </div>
          ) : null}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reference" className="sr-only">Organization ID or Service ID or Identifier</Label>
              <Input
                id="reference"
                className="h-11 rounded-full border-0 bg-primary/15 px-5 text-center shadow-none placeholder:text-primary/45 focus-visible:ring-primary/35"
                placeholder="Organization ID or Identifier"
                autoComplete="off"
                spellCheck={false}
                disabled={isLoading}
                {...register('reference')}
                aria-invalid={!!errors.reference}
              />
              {errors.reference ? <p className="text-center text-sm text-destructive">{errors.reference.message}</p> : null}
            </div>

            <Button type="submit" className="mx-auto flex h-11 min-w-44 rounded-full px-8" disabled={isLoading}>
              {isLoading ? <Spinner className="mr-2" /> : <Search className="mr-2 h-4 w-4" />}
              Continue
            </Button>
          </form>
          <AuthTermsDisclosure
            termsText={termsText}
            privacyText={privacyText}
            className="mt-5 text-center"
          />

          <div className="mt-8 text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to={loginHref} className="font-semibold text-primary hover:underline">
              Sign in
            </Link>
            <div className="mt-3 flex items-center justify-center gap-3 text-xs">
              <Link to={vendorSignupHref} className="inline-flex items-center gap-1 font-medium text-muted-foreground hover:text-primary">
                Vendor <ChevronRight className="h-3 w-3" />
              </Link>
              {canShowOrganizationSignup ? (
                <Link to="/signup/company" className="inline-flex items-center gap-1 font-medium text-muted-foreground hover:text-primary">
                  Organization <ChevronRight className="h-3 w-3" />
                </Link>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>
    </TenantAuthShell>
  )
}
